import { createContext, ReactNode, useState } from "react";
import { notaFiscalProduto } from "../interfaces/notaFiscal/notaFiscalProduto";
import { notaFiscalInfo } from "../interfaces/notaFiscal/notaFiscalInfo";
import { composeUniqueKey, parseNotaFiscalXmlFileContent } from "../utils/notaFiscalUtils";
import { playErrorAudio } from "../utils/audioUtils";

interface FileContextType {
  notaFiscalProductsArray: notaFiscalProduto[];
  notaFiscalInformations: notaFiscalInfo | null;
  handleXmlFileImport: (file: File, onFinish?: (isValidXml: boolean) => void) => void;
  handleXmlFileImportWithScannerCheck: (file: File, scannerArray: notaFiscalProduto[], onFinish?: (isValidXml: boolean) => void) => void;
  areXmlTagsValid: boolean;
  xmlFileImportsCount: number;
}

export const FileContext = createContext<FileContextType>({
  xmlFileImportsCount: 0,
  notaFiscalProductsArray: [],
  notaFiscalInformations: null,
  handleXmlFileImport: () => {},
  handleXmlFileImportWithScannerCheck: () => {},
  areXmlTagsValid: true,
});

export default function FileContextProvider({ children }: { children: ReactNode }) {
  const notaFiscalNamespace = "http://www.portalfiscal.inf.br/nfe";

  const [notaFiscalProducts, setNotaFiscalProducts] = useState<notaFiscalProduto[]>([]);
  const [notaFiscalInformations, setNotaFiscalInformations] = useState<notaFiscalInfo | null>(null);
  const [areXmlTagsValid, setAreXmlTagsValid] = useState(true);
  const [xmlFileImportsCount, setXmlFileImportsCount] = useState<number>(0);

  function processFileRead<T>(file: File, afterImportCheck?: (array: T[]) => void, onFinish?: (isValidXml: boolean) => void) {
    const reader = new FileReader();

    reader.onload = (fileEvent) => {
      const xmlFileContent = fileEvent.target?.result as string;
      const { tagsAreValid, informations, productsArray } = parseNotaFiscalXmlFileContent(notaFiscalNamespace, xmlFileContent);

      setAreXmlTagsValid(tagsAreValid);
      setXmlFileImportsCount((prev) => prev + 1);

      if (!tagsAreValid) {
        onFinish?.(false);
        return;
      }

      if (informations) {
        setNotaFiscalInformations(informations);
        setNotaFiscalProducts(productsArray);
      }

      if (afterImportCheck) {
        afterImportCheck(productsArray as T[]);
      }

      onFinish?.(true);
    };

    reader.readAsText(file);
  }

  function handleXmlFileImport(file: File, onFinish?: (isValidXml: boolean) => void) {
    processFileRead(file, undefined, onFinish);
  }

  function handleXmlFileImportWithScannerCheck(file: File, scannerArray: notaFiscalProduto[], onFinish?: (isValidXml: boolean) => void) {
    processFileRead<notaFiscalProduto>(
      file,
      (importedItems) => {
        const xmlProductIdentifiers = new Set<string>(importedItems.map((xmlItem) => composeUniqueKey(xmlItem)));
        const hasNotFoundItensOnScanner = scannerArray.some((item) => !xmlProductIdentifiers.has(composeUniqueKey(item)));

        if (hasNotFoundItensOnScanner) {
          playErrorAudio();
        }
      },
      onFinish,
    );
  }

  const values: FileContextType = {
    notaFiscalProductsArray: notaFiscalProducts,
    notaFiscalInformations,
    areXmlTagsValid,
    xmlFileImportsCount,
    handleXmlFileImport,
    handleXmlFileImportWithScannerCheck,
  };

  return <FileContext.Provider value={values}>{children}</FileContext.Provider>;
}
